(function () {
  if (typeof window !== 'undefined' && window.CartSchedule) {
    return;
  }

  const DEFAULT_VALUE = 'No preference';
  const DATE_ATTRIBUTE = 'Delivery/Pickup Date';
  const SLOT_ATTRIBUTE = 'Delivery/Pickup Time Slot';
  const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const SLOT_VALUES = [
    '10:00AM to 12:00PM',
    '12:00PM to 2:00PM',
    '2:00PM to 4:00PM',
    '4:00PM to 6:00PM',
    '6:00PM to 8:00PM',
    '7:00PM to 9:00PM',
  ];
  const LEGACY_SLOT_VALUES = {
    '10-12': '10:00AM to 12:00PM',
    '12-2': '12:00PM to 2:00PM',
    '2-4': '2:00PM to 4:00PM',
    '4-6': '4:00PM to 6:00PM',
    '6-8': '6:00PM to 8:00PM',
    '7-9': '7:00PM to 9:00PM',
  };
  const ROOT_SELECTOR = '[data-cart-schedule-root]';
  const DATE_SELECTOR = '[data-cart-schedule-date]';
  const SLOT_SELECTOR = '[data-cart-schedule-slot]';
  const ERROR_SELECTOR = '[data-cart-schedule-error]';
  const CHECKOUT_BUTTON_SELECTOR = '#checkout, #CartDrawer-Checkout';
  const SAVE_ERROR_MESSAGE = "We couldn't save your delivery/pickup details. Please try again.";
  const REQUIRED_ERROR_MESSAGE = 'Please select your delivery/pickup date and time slot before checkout.';

  const state = {
    initialized: false,
    startDate: null,
    dateCount: 14,
    date: DEFAULT_VALUE,
    slot: DEFAULT_VALUE,
  };

  let pendingSave = Promise.resolve(true);
  let lastSavedSignature = null;

  function parseIsoDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));

    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      return null;
    }

    return date;
  }

  function formatIsoDate(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  function formatFriendlyDate(date) {
    const dayOfMonth = String(date.getUTCDate()).padStart(2, '0');
    const month = MONTH_LABELS[date.getUTCMonth()];
    const day = DAY_LABELS[date.getUTCDay()];

    return `${dayOfMonth} ${month} (${day})`;
  }

  function formatDateLabel(date, locale = 'en') {
    if (/^en(?:-|$)/i.test(locale)) {
      return formatFriendlyDate(date);
    }

    try {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'short', month: 'short', day: '2-digit', timeZone: 'UTC',
      }).format(date);
    } catch (error) {
      return formatIsoDate(date);
    }
  }

  function formatTimeSlotLabel(value, locale = 'en') {
    const match = /^(\d{1,2}):(\d{2})(AM|PM) to (\d{1,2}):(\d{2})(AM|PM)$/.exec(value);

    if (!match) {
      return value;
    }

    const formatTime = (hour, minute, period) => {
      const hour24 = Number(hour) % 12 + (period === 'PM' ? 12 : 0);
      const date = new Date(Date.UTC(2000, 0, 1, hour24, Number(minute)));

      try {
        return new Intl.DateTimeFormat(locale, {
          hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'UTC',
        }).format(date);
      } catch (error) {
        return `${String(hour24).padStart(2, '0')}:${minute}`;
      }
    };

    return `${formatTime(match[1], match[2], match[3])} – ${formatTime(match[4], match[5], match[6])}`;
  }

  function buildDateOptions(startDateValue, count, locale = 'en') {
    const startDate = parseIsoDate(startDateValue);
    const total = Number(count);

    if (!startDate || !Number.isInteger(total) || total < 1) {
      return [];
    }

    const options = [];

    for (let offset = 0; offset < total; offset += 1) {
      const optionDate = new Date(startDate.getTime());
      optionDate.setUTCDate(startDate.getUTCDate() + offset);
      const isoValue = formatIsoDate(optionDate);
      const value = formatFriendlyDate(optionDate);

      options.push({ value, label: formatDateLabel(optionDate, locale), isoValue });
    }

    return options;
  }

  function normalizeScheduleValues(dateValue, slotValue, startDateValue, count) {
    const dateOptions = buildDateOptions(startDateValue, count);
    const validDates = new Set(dateOptions.map((option) => option.value));
    const legacyDateValues = new Map(dateOptions.map((option) => [option.isoValue, option.value]));
    const validSlots = new Set(SLOT_VALUES);
    const normalizedDate = validDates.has(dateValue)
      ? dateValue
      : legacyDateValues.get(dateValue) || DEFAULT_VALUE;
    const normalizedSlot = validSlots.has(slotValue)
      ? slotValue
      : LEGACY_SLOT_VALUES[slotValue] || DEFAULT_VALUE;

    return {
      date: normalizedDate,
      slot: normalizedSlot,
    };
  }

  function resolveInitialSelections(savedDate, savedSlot, liveDate, liveSlot, startDateValue, count) {
    const preferredDate = liveDate && liveDate !== DEFAULT_VALUE ? liveDate : savedDate;
    const preferredSlot = liveSlot && liveSlot !== DEFAULT_VALUE ? liveSlot : savedSlot;

    return normalizeScheduleValues(preferredDate, preferredSlot, startDateValue, count);
  }

  function getRoots() {
    if (typeof document === 'undefined') {
      return [];
    }

    return Array.from(document.querySelectorAll(ROOT_SELECTOR));
  }

  function getStateSignature(values) {
    return `${values.date}||${values.slot}`;
  }

  function syncSelectValue(select, selectedValue) {
    select.value = selectedValue;

    if (select.value !== selectedValue) {
      select.value = DEFAULT_VALUE;
    }
  }

  function clearErrors() {
    getRoots().forEach((root) => {
      const error = root.querySelector(ERROR_SELECTOR);

      if (!error) {
        return;
      }

      error.hidden = true;
      error.textContent = '';
    });
  }

  function errorMessageForRoot(root, kind) {
    return kind === 'required'
      ? root.dataset.requiredError || REQUIRED_ERROR_MESSAGE
      : root.dataset.saveError || SAVE_ERROR_MESSAGE;
  }

  function showError(kind) {
    getRoots().forEach((root) => {
      const error = root.querySelector(ERROR_SELECTOR);

      if (!error) {
        return;
      }

      error.hidden = false;
      error.textContent = errorMessageForRoot(root, kind);
    });
  }

  function syncRoot(root) {
    const dateSelect = root.querySelector(DATE_SELECTOR);
    const slotSelect = root.querySelector(SLOT_SELECTOR);

    if (!dateSelect || !slotSelect) {
      return;
    }

    const locale = root.dataset.locale || 'en';
    const dateLabels = new Map(buildDateOptions(root.dataset.startDate, root.dataset.dateCount, locale)
      .map((option) => [option.value, option.label]));

    Array.from(dateSelect.options).forEach((option) => {
      if (dateLabels.has(option.value)) {
        option.textContent = dateLabels.get(option.value);
      }
    });
    Array.from(slotSelect.options).forEach((option) => {
      if (SLOT_VALUES.includes(option.value)) {
        option.textContent = formatTimeSlotLabel(option.value, locale);
      }
    });

    syncSelectValue(dateSelect, state.date);
    syncSelectValue(slotSelect, state.slot);

    root.dataset.selectedDate = state.date;
    root.dataset.selectedSlot = state.slot;
  }

  function syncAllRoots() {
    getRoots().forEach(syncRoot);
  }

  function getRootConfig(root) {
    return {
      startDate: root.dataset.startDate || null,
      dateCount: Number.parseInt(root.dataset.dateCount || '14', 10),
      selectedDate: root.dataset.selectedDate || DEFAULT_VALUE,
      selectedSlot: root.dataset.selectedSlot || DEFAULT_VALUE,
      hasSavedDate: root.dataset.hasSavedDate === 'true',
      hasSavedSlot: root.dataset.hasSavedSlot === 'true',
    };
  }

  function mountRoots() {
    const roots = getRoots();

    if (!roots.length) {
      return;
    }

    let shouldPersistDefaults = false;

    if (!state.initialized) {
      const rootConfig = getRootConfig(roots[0]);
      const initialDateValue = roots[0].querySelector(DATE_SELECTOR);
      const initialSlotValue = roots[0].querySelector(SLOT_SELECTOR);
      const normalized = resolveInitialSelections(
        rootConfig.selectedDate,
        rootConfig.selectedSlot,
        initialDateValue ? initialDateValue.value : DEFAULT_VALUE,
        initialSlotValue ? initialSlotValue.value : DEFAULT_VALUE,
        rootConfig.startDate,
        rootConfig.dateCount
      );

      state.initialized = true;
      state.startDate = rootConfig.startDate;
      state.dateCount = rootConfig.dateCount;
      state.date = normalized.date;
      state.slot = normalized.slot;

      shouldPersistDefaults =
        !rootConfig.hasSavedDate ||
        !rootConfig.hasSavedSlot ||
        normalized.date !== rootConfig.selectedDate ||
        normalized.slot !== rootConfig.selectedSlot;

      if (!shouldPersistDefaults) {
        lastSavedSignature = getStateSignature(normalized);
      }
    }

    syncAllRoots();

    if (shouldPersistDefaults) {
      void queueSave();
    }
  }

  function getCartUpdateUrl() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

    return new URL('cart/update.js', `${window.location.origin}${root}`).toString();
  }

  async function performSave() {
    const currentValues = normalizeScheduleValues(state.date, state.slot, state.startDate, state.dateCount);
    const currentSignature = getStateSignature(currentValues);

    state.date = currentValues.date;
    state.slot = currentValues.slot;
    syncAllRoots();

    if (currentSignature === lastSavedSignature) {
      clearErrors();
      return true;
    }

    const payload = {
      attributes: {
        [DATE_ATTRIBUTE]: currentValues.date,
        [SLOT_ATTRIBUTE]: currentValues.slot,
      },
    };

    const response = await fetch(getCartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Cart attribute save failed with status ${response.status}`);
    }

    lastSavedSignature = currentSignature;
    clearErrors();

    return true;
  }

  function queueSave() {
    const runSave = async () => {
      try {
        return await performSave();
      } catch (error) {
        showError('save');
        return false;
      }
    };

    pendingSave = pendingSave.then(runSave, runSave);

    return pendingSave;
  }

  function updateStateFromRoot(root) {
    const dateSelect = root.querySelector(DATE_SELECTOR);
    const slotSelect = root.querySelector(SLOT_SELECTOR);
    const normalized = normalizeScheduleValues(
      dateSelect ? dateSelect.value : DEFAULT_VALUE,
      slotSelect ? slotSelect.value : DEFAULT_VALUE,
      state.startDate,
      state.dateCount
    );

    state.date = normalized.date;
    state.slot = normalized.slot;
    syncAllRoots();
  }

  function handleChange(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    if (!event.target.matches(DATE_SELECTOR) && !event.target.matches(SLOT_SELECTOR)) {
      return;
    }

    const root = event.target.closest(ROOT_SELECTOR);

    if (!root) {
      return;
    }

    updateStateFromRoot(root);
    void queueSave();
  }

  function submitCheckoutForm(button) {
    if (!(button instanceof HTMLButtonElement) || !button.form) {
      return;
    }

    if (typeof button.form.requestSubmit === 'function') {
      button.form.requestSubmit(button);
      return;
    }

    const fallbackInput = document.createElement('input');

    fallbackInput.type = 'hidden';
    fallbackInput.name = button.name || 'checkout';
    fallbackInput.value = button.value || '';
    button.form.appendChild(fallbackInput);
    button.form.submit();
  }

  function validateRequiredSchedule() {
  const currentValues = normalizeScheduleValues(
    state.date,
    state.slot,
    state.startDate,
    state.dateCount
  );

  state.date = currentValues.date;
  state.slot = currentValues.slot;
  syncAllRoots();

  const hasDate = currentValues.date && currentValues.date !== DEFAULT_VALUE;
  const hasSlot = currentValues.slot && currentValues.slot !== DEFAULT_VALUE;

  if (hasDate && hasSlot) {
    clearErrors();
    return true;
  }

  showError('required');

  const firstRoot = getRoots()[0];

  if (firstRoot) {
    const fieldToFocus = !hasDate
      ? firstRoot.querySelector(DATE_SELECTOR)
      : firstRoot.querySelector(SLOT_SELECTOR);

    if (fieldToFocus && typeof fieldToFocus.focus === 'function') {
      fieldToFocus.focus();
    }
  }

  return false;
}

  async function handleCheckoutClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const button = event.target.closest(CHECKOUT_BUTTON_SELECTOR);

    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    if (!getRoots().length || button.dataset.cartScheduleSubmitting === 'true') {
      return;
    }

    event.preventDefault();
mountRoots();

if (!validateRequiredSchedule()) {
  return;
}

button.dataset.cartScheduleSubmitting = 'true';
button.disabled = true;
button.setAttribute('aria-busy', 'true');

const ok = await queueSave();

    if (ok) {
      button.disabled = false;
      submitCheckoutForm(button);
      return;
    }

    button.dataset.cartScheduleSubmitting = 'false';
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }

  function handleMutations(mutations) {
    const addedScheduleMarkup = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) {
          return false;
        }

        return node.matches(ROOT_SELECTOR) || Boolean(node.querySelector(ROOT_SELECTOR));
      })
    );

    if (addedScheduleMarkup) {
      mountRoots();
    }
  }

  function start() {
    if (typeof document === 'undefined' || document.body == null) {
      return;
    }

    mountRoots();
    document.addEventListener('change', handleChange);
    document.addEventListener('click', handleCheckoutClick);
    document.addEventListener('shopify:section:load', mountRoots);

    const observer = new MutationObserver(handleMutations);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  const api = {
    DEFAULT_VALUE,
    SLOT_VALUES,
    buildDateOptions,
    formatDateLabel,
    formatTimeSlotLabel,
    errorMessageForRoot,
    normalizeScheduleValues,
    resolveInitialSelections,
  };

  if (typeof window !== 'undefined') {
    window.CartSchedule = api;
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})();
